export const CHECKOUT_ERROR_STAGES = {
  CLICK: 'click',
  SESSION_CREATION: 'session_creation',
  MERCHANT_VALIDATION: 'merchant_validation',
  CHECKOUT_CREATION: 'checkout_creation',
  SHIPPING_CONTACT_UPDATE: 'shipping_contact_update',
  SHIPPING_METHOD_UPDATE: 'shipping_method_update',
  SHIPPING_BLOCKED_AUTHORIZATION: 'shipping_blocked_authorization',
  PAYMENT_AUTHORIZATION: 'payment_authorization',
  SESSION_BEGIN: 'session_begin',
} as const

export const CHECKOUT_ERROR_TYPES = {
  DECLINE: 'decline',
  API_ERROR: 'api_error',
  VALIDATION_ERROR: 'validation_error',
  NETWORK_ERROR: 'network_error',
  CLIENT_ERROR: 'client_error',
  UNKNOWN: 'unknown',
} as const

export const CHECKOUT_API_NAMES = {
  UNIFIED_PAYMENT_GATEWAY: 'unified-payment-gateway',
  CHECKOUT_SERVICE: 'checkout-service',
} as const

export interface CheckoutErrorContext {
  error_reason?: string
  error_message?: string
  error_type: string
  error_stage?: string
  error_origin?: string
  http_status?: number
  error_code?: string
  api_endpoint?: string
}

const DECLINE_PATTERN = /(declin|refus|insufficient|fonds insuffisants|do not honor|card was declined)/i
const VALIDATION_PATTERN = /(enter a valid|not a valid|invalid|valid discount|valid address|is required)/i

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined

const asNumber = (value: unknown): number | undefined => (typeof value === 'number' ? value : undefined)

interface ExtractedError {
  message?: string
  backendMessage?: string
  httpStatus?: number
  errorCode?: string
  endpoint?: string
  isHttpError: boolean
}

const sanitizeEndpoint = (url: string | undefined): string | undefined => url?.split('?')[0]

const extractError = (error: unknown): ExtractedError => {
  const record = asRecord(error)
  if (!record) {
    return { message: asString(error), isHttpError: false }
  }

  const response = asRecord(record.response)
  const responseData = asRecord(response?.data) ?? asRecord(record.gatewayResponse)
  const httpStatus = asNumber(record.httpStatus) ?? asNumber(response?.status)
  const errorCode = asString(record.errorCode) ?? asString(responseData?.code) ?? asString(responseData?.errorCode)
  const endpoint = sanitizeEndpoint(asString(record.endpoint) ?? asString(asRecord(record.config)?.url))

  return {
    message: asString(record.message),
    backendMessage: asString(responseData?.message),
    httpStatus,
    errorCode,
    endpoint,
    isHttpError: record.isAxiosError === true || httpStatus !== undefined || 'gatewayResponse' in record,
  }
}

const classify = (extracted: ExtractedError): string => {
  const text = `${extracted.backendMessage ?? ''} ${extracted.message ?? ''}`.trim()
  if (text && DECLINE_PATTERN.test(text)) return CHECKOUT_ERROR_TYPES.DECLINE
  if (text && VALIDATION_PATTERN.test(text)) return CHECKOUT_ERROR_TYPES.VALIDATION_ERROR
  if (extracted.httpStatus !== undefined) return CHECKOUT_ERROR_TYPES.API_ERROR
  if (extracted.isHttpError) return CHECKOUT_ERROR_TYPES.NETWORK_ERROR
  if (extracted.message) return CHECKOUT_ERROR_TYPES.CLIENT_ERROR
  return CHECKOUT_ERROR_TYPES.UNKNOWN
}

export const buildCheckoutErrorContext = (
  error: unknown,
  opts: { stage?: string; apiName?: string; errorReason?: string } = {},
): CheckoutErrorContext => {
  const extracted = extractError(error)
  const errorMessage = extracted.backendMessage ?? extracted.message

  return {
    error_reason: opts.errorReason ?? errorMessage,
    error_message: errorMessage,
    error_type: classify(extracted),
    error_stage: opts.stage,
    error_origin: opts.apiName,
    http_status: extracted.httpStatus,
    error_code: extracted.errorCode,
    api_endpoint: extracted.endpoint,
  }
}
